import { styled } from 'baseui'
import { FlexBetween, FlexColumn } from '@/shared/theme/styled-components'
import { RAIL_EASE, RAIL_SLOT_SIZE, railIconHover } from '@/shared/theme/railSlot'

const MODULE_MOTION = `200ms ${RAIL_EASE}`

export const ToggleButton = styled('button', ({ $theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '2rem',
  height: '2rem',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  border: 0,
  borderRadius: '50%',
  backgroundColor: 'transparent',
  color: $theme.colors.mono500,
  cursor: 'pointer',
  ':hover': { color: $theme.colors.primary, backgroundColor: $theme.colors.bg050 },
  ':focus-visible': { outline: `2px solid ${$theme.colors.primary}`, outlineOffset: '2px' },
  ':not(#_) svg': { transition: `transform ${MODULE_MOTION}` },
  ':is([aria-expanded="true"]) svg': { transform: 'rotate(180deg)' },
  '@media (prefers-reduced-motion: reduce)': { ':not(#_) svg': { transition: 'none' } },
}))

// The vertical padding gives a folded module the height of the "schedule a callback" button.
export const StyledModuleHeader = styled<typeof FlexBetween, { $clickable: boolean; $highlighted: boolean }>(
  FlexBetween,
  ({ $theme, $clickable, $highlighted }) => ({
    width: '100%',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    paddingLeft: '0.9375rem',
    paddingRight: '0.9375rem',
    backgroundColor: $highlighted ? $theme.colors.primaryPulse : 'transparent',
    cursor: $clickable ? 'pointer' : 'default',
    userSelect: $clickable ? 'none' : 'auto',
  }),
)

export const ModuleTitleGroup = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.625rem',
  minWidth: 0,
})

export const ModuleIcon = styled<'span', { $highlighted?: boolean }>('span', ({ $theme, $highlighted }) => ({
  position: 'relative',
  display: 'grid',
  placeItems: 'center',
  width: '1.875rem',
  height: '1.875rem',
  flexShrink: 0,
  ...($highlighted ? { borderRadius: '999px', backgroundColor: $theme.colors.primaryPulseHover } : {}),
}))

export const NotificationBadge = styled('span', ({ $theme }) => ({
  position: 'absolute',
  top: '-0.375rem',
  right: '-0.375rem',
  minWidth: '1.125rem',
  height: '1.125rem',
  paddingLeft: '0.25rem',
  paddingRight: '0.25rem',
  borderRadius: '999px',
  backgroundColor: $theme.colors.negative500,
  color: $theme.colors.white,
  fontSize: '0.6875rem',
  fontWeight: 700,
  lineHeight: '1.125rem',
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
  boxShadow: `0 0 0 0.125rem ${$theme.colors.white}`,
}))

export const StatusDot = styled('span', ({ $theme }) => ({
  position: 'absolute',
  top: '-0.125rem',
  right: '-0.125rem',
  width: '0.625rem',
  height: '0.625rem',
  borderRadius: '50%',
  backgroundColor: $theme.colors.negative400,
  boxShadow: `0 0 0 0.125rem ${$theme.colors.white}`,
}))

export const RailHeader = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
})

export const RailButton = styled('button', ({ $theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: RAIL_SLOT_SIZE,
  height: RAIL_SLOT_SIZE,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  border: 0,
  borderRadius: '0.5rem',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'background-color 0.12s',
  ':hover': { backgroundColor: $theme.colors.bg050 },
  ...railIconHover,
  ':focus-visible': { outline: `2px solid ${$theme.colors.primary}`, outlineOffset: '2px' },
}))

export const ModuleTitle = styled<'h3', { $highlighted?: boolean }>('h3', ({ $theme, $highlighted }) => ({
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: $highlighted ? $theme.colors.white : $theme.colors.contentPrimary,
}))

export const ModuleIndicator = styled('span', ({ $theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: $theme.colors.contentPrimary,
}))

export const HeaderActions = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
})

// Sets its own display, so the `hidden` attribute folds it only through `:is([hidden])`.
export const ModuleContent = styled('div', {
  display: 'grid',
  gridTemplateRows: '1fr',
  width: '100%',
  animationName: { from: { gridTemplateRows: '0fr', opacity: 0 }, to: { gridTemplateRows: '1fr', opacity: 1 } },
  animationDuration: '260ms',
  animationTimingFunction: RAIL_EASE,
  transition: `grid-template-rows ${MODULE_MOTION}, opacity ${MODULE_MOTION}, display 200ms allow-discrete`,
  ':is([hidden])': { display: 'none', gridTemplateRows: '0fr', opacity: 0 },
  ':not(#_) > *': {
    minHeight: 0,
    overflow: 'hidden',
    transition: `padding ${MODULE_MOTION}`,
    animationName: { from: { paddingTop: 0, paddingBottom: 0 } },
    animationDuration: '260ms',
    animationTimingFunction: RAIL_EASE,
  },
  ':is([hidden]) > *': { paddingTop: 0, paddingBottom: 0 },
  '@media (prefers-reduced-motion: reduce)': { animationName: 'none', transition: 'none' },
})

export const ModuleBody = styled<typeof FlexColumn, { $gap?: string }>(FlexColumn, ({ $gap = '1rem' }) => ({
  width: '100%',
  boxSizing: 'border-box',
  gap: $gap,
  paddingTop: 0,
  paddingBottom: '1rem',
  paddingLeft: '1rem',
  paddingRight: '1rem',
}))
